/**
 * Componente para visualizar reportes de BI (Business Intelligence)
 * 
 * Este componente permite mostrar reportes de Power BI embebidos en la aplicación.
 * Utiliza un menú hamburguesa flotante para navegar entre diferentes reportes.
 * 
 * Características principales:
 * - Muestra reportes con menú hamburguesa flotante para fácil navegación
 * - Carga dinámica de reportes al seleccionar una opción
 * - Manejo de errores en la carga de reportes
 * - Diseño responsive que se adapta al tamaño de la pantalla
 */

import React, { useEffect, useState } from 'react';
import Reporte from '../vista/reporteVista';
import type { IReporte } from '../interfaces';
import { useCargando } from '../../../globales/contextos/cargandoContext';
import { useSearchParams } from 'react-router-dom';
import NotFound from '../../paginaNoEncontrada/paginaNoEncontrada';
import { usePermisos } from '../../../globales/contextos/usePermisos';

/**
 * Componente principal que maneja la lógica de visualización de reportes
 * 
 * Funcionamiento:
 * 1. Muestra un menú hamburguesa flotante con la lista de reportes
 * 2. Al seleccionar una opción del menú, carga el reporte correspondiente
 * 3. El menú se cierra automáticamente al seleccionar una opción
 * 4. Maneja los errores que puedan ocurrir durante la carga
 * 
 */

export interface IReporteCVProps {
    listaReportes: IReporte[];
}

const ReporteCV: React.FC<IReporteCVProps> = ({ listaReportes }) => {
    const { mostrarCargando, ocultarCargando } = useCargando();
    const { puedeAccederModulo } = usePermisos();
    const [searchParams] = useSearchParams();

    const [urlCompleta, setUrlCompleta] = useState<string>('');
    const [error, setError] = useState<string>('');

    // Filtrar solo los reportes a los que el usuario tiene acceso
    const reportesVisibles = listaReportes.filter(r => puedeAccederModulo(r.permisoRequerido));

    const reporteActivo = searchParams.get('reporte') || reportesVisibles[0]?.slug || '';

    // Si hay un slug en la URL pero no existe o no tiene permiso → 404
    const slugEnUrl = searchParams.get('reporte');
    const slugValido = !slugEnUrl || reportesVisibles.some(r => r.slug === slugEnUrl);

    const cargarReporte = (slug: string) => {
        try {
            const reporte = reportesVisibles.find(r => r.slug === slug);
            if (!reporte) return;

            mostrarCargando('Cargando reporte...', false);
            const url = new URL(reporte.url);
            url.searchParams.append('pageView', '1');
            setUrlCompleta(url.toString());

            setTimeout(() => ocultarCargando(), 800);
        } catch (err) {
            console.error('Error al construir la URL:', err);
            setError('Error al cargar el reporte');
            ocultarCargando();
        }
    };

    useEffect(() => {
        if (reporteActivo) {
            cargarReporte(reporteActivo);
        }
    }, [reporteActivo, listaReportes]);

    if (!listaReportes || listaReportes.length === 0) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>No hay reportes disponibles</div>;
    }

    if (!slugValido) {
        return <NotFound />;
    }

    if (error) {
        return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
    }

    const reporteActivoNombre = reportesVisibles.find(r => r.slug === reporteActivo)?.nombre || '';

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Título del reporte activo */}
            <div style={{
                padding: '20px 20px 10px 20px',
                fontSize: '18px',
                fontWeight: 700,
                color: '#000'
            }}>
                {reporteActivoNombre}
            </div>

            {/* Contenedor del reporte */}
            <div style={{ flex: 1, minHeight: 0 }}>
                {urlCompleta && (
                    <Reporte
                        url={urlCompleta}
                        height="100%"
                        width="100%"
                    />
                )}
            </div>
        </div>
    );
};

export default ReporteCV; 